/**
 * Fold German letters to their ASCII transliteration and strip diacritics.
 *
 * Lowercases the input, transliterates the German umlauts and eszett
 * (ae/oe/ue/ss), then removes any remaining combining marks via Unicode NFD
 * decomposition. This is the single shared normalisation step used by both
 * {@link slugify} (URL slugs) and the search index
 * ({@link ../lib/search.ts} `normalizeSearchText`), so a German query matches
 * German copy exactly the way a German title slugifies.
 */
export function foldGermanChars(value: string): string {
  return value
    .toLowerCase()
    .replace(/ä/gu, 'ae')
    .replace(/ö/gu, 'oe')
    .replace(/ü/gu, 'ue')
    .replace(/ß/gu, 'ss')
    .normalize('NFD')
    // Strip the combining diacritical marks block (U+0300-U+036F) left by NFD
    // decomposition. Explicit \u escapes (not literal combining-mark bytes) so
    // editor/encoding normalization can never silently alter the range.
    .replace(/[\u0300-\u036f]/gu, '')
}

/**
 * Slugify a human title into a stable, URL-safe kebab-case slug.
 *
 * Lives in its own module so content modules can share it without creating an
 * import cycle through {@link ./catalog.ts}. German umlauts are transliterated
 * (ae/oe/ue/ss) before diacritics are stripped, so German titles slugify the
 * same way they always did.
 */
export function slugify(value: string): string {
  return foldGermanChars(value)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}
